/**
 * The program registry (ADR-0050). One entry per certification program;
 * values reproduce the behavior the hand-coded sites had before V102-1-4,
 * except the acceptance rule (ADR-0050 §3), which is settled here once.
 */
import { z } from 'zod';
import { validateProgramRegistry, } from './manifest.js';
import { requirementSourceProfileProblem } from './requirement-source.js';
import { FedrampProfileOptions, FedrampProfileOptionsInput } from './fedramp-options.js';
const NOT_FOR_THIS_PROGRAM = 'class, path and report_type do not apply to this program.';
const noProgramColumns = {
    report_type: { rule: 'forbidden', message: NOT_FOR_THIS_PROGRAM },
    certification_class: { rule: 'forbidden', message: NOT_FOR_THIS_PROGRAM },
    path: { rule: 'forbidden', message: NOT_FOR_THIS_PROGRAM },
};
const minRetention90 = () => 90;
const noSuffix = () => null;
/**
 * SOC 2 options (ADR-0078 §1, V106-1-12): the optional Trust Services
 * Categories in the examination (Security is always in scope) and, with
 * Privacy, the entity's privacy role (`both` when unset). Existing profiles
 * store `{}`, which reads as Security only.
 */
export const SOC2_OPTIONAL_CATEGORIES = ['availability', 'confidentiality', 'processing_integrity', 'privacy'];
export const Soc2ProfileOptions = z
    .object({
    categories: z
        .array(z.enum(SOC2_OPTIONAL_CATEGORIES, { error: 'SOC 2 categories are availability, confidentiality, processing_integrity or privacy (Security is always in scope).' }))
        .max(SOC2_OPTIONAL_CATEGORIES.length)
        .refine((a) => new Set(a).size === a.length, { message: 'List each SOC 2 category once.' })
        .optional(),
    privacy_role: z.enum(['controller', 'processor', 'both'], { error: 'Privacy role must be controller, processor or both.' }).optional(),
})
    .strict()
    .refine((o) => !o.privacy_role || (o.categories ?? []).includes('privacy'), { message: 'A privacy role applies only when Privacy is in scope.' });
const soc2 = {
    key: 'soc2',
    manifestVersion: 1,
    lifecycle: 'active',
    label: 'SOC 2',
    shortLabel: 'SOC 2',
    order: 0,
    frameworkId: 'soc2',
    profile: {
        columns: {
            report_type: { rule: 'required', values: ['type1', 'type2'], message: 'SOC 2 profiles need report_type type1 or type2.' },
            certification_class: { rule: 'forbidden', message: 'class/path apply to FedRAMP only.' },
            path: { rule: 'forbidden', message: 'class/path apply to FedRAMP only.' },
        },
        // V106-1-12: Trust Services Categories in scope + privacy role (ADR-0078 §1).
        options: Soc2ProfileOptions,
        minRetentionDays: minRetention90,
    },
    period: { model: 'soc2_report_type', maxMonths: 12 },
    // ADR-0100 §1 (O-1, owner 2026-10-22): a system-scoped program assesses the CIs its in-scope systems link
    // (one synthesized `assessed` asset per CI-in-system); AI machine determinations judge those cells.
    resourceScope: 'linked_configuration_items',
    stage: { model: 'periods', countsInForceObligations: false },
    // ADR-0078 §4: points of focus on the objective model. The engine actually used is the
    // current approved `soc2-readiness` rule set (migration 508 files {"engine":"objective"});
    // with none approved the legacy `framework` engine runs (every criterion, SEC-* crediting
    // edges), and consumers that read the objective model follow the switch
    // (`frameworkEngineSwitchedOff` / `effectiveObjectiveMode`). Revert = the next version
    // naming `framework`.
    readiness: {
        engine: 'objective',
        objectiveMode: 'objectives',
        legacyEngine: 'framework',
        switchRuleSet: 'soc2-readiness',
        // D-106-18: once the switch runs the objective engine, the hourly evaluator proposes results
        // from linked evidence; a person reviews every one (ADR-0056 §6).
        scheduledEvaluation: true,
        // ADR-0079 §7: Security always; a criterion of another category only when chosen; a CC
        // point tied to another category only when that category is chosen; privacy points by role.
        applicability: {
            option: 'categories',
            always: ['security'],
            requirementKey: 'category',
            objectiveKey: 'requires_category',
            match: [{ objectiveKey: 'privacy_role', option: 'privacy_role', any: 'both' }],
        },
    },
    // ADR-0078 §2 / ADR-0079 §3: one program control per in-scope criterion.
    requirementSource: { kind: 'framework', programControls: {} },
    scoring: { engine: 'none' },
    deadlines: { vulnerability: 'ops_sla' },
    coverage: { documentKinds: ['soc2_type2', 'soc2_type1', 'bridge_letter'], subserviceOrgs: true, federalDataGaps: false },
    operate: { queue: true, contributors: ['findings', 'acceptances'] },
    acceptanceSources: ['risk_register'],
    defaultForUntaggedWeakness: false,
    packages: { kinds: ['soc2_pbc_list', 'soc2_risk_assessment'], rulesGated: false },
    features: { significantChangeNotices: false, cds: false, legalDeterminations: false, derivedSweep: false, poam: false, customerRequirements: false },
    assessorTypes: ['cpa'],
    onboarding: {
        hint: 'Report for customers', addProgramLabel: 'Set up SOC 2 Type II', addProgramDefaults: { report_type: 'type2' },
        // V108-8-15: asked when SOC 2 is added; a change once a period exists starts a new period (determinations carry).
        askOptions: [
            { key: 'report_type', label: 'Report type', newPeriodOnChange: true, values: [
                    { value: 'type1', label: 'Type I · controls on one date' }, { value: 'type2', label: 'Type II · controls over 3–12 months' },
                ] },
        ],
    },
    shell: {
        notApplicable: {}, titles: {}, loopLabel: null, defaultTab: 'stage', operateGate: false,
        // SOC 2 readiness is rehosted on Prepare / Audit / Operate (V106-1-8: the points-of-focus panels once the
        // soc2-readiness switch is approved, today's framework view before); Scope adds the categories.
        panels: { scope: 'soc2-scope', prepare: 'soc2-readiness', audit: 'soc2-readiness', operate: 'soc2-readiness' },
    },
    nav: {
        titleSuffix: (p) => (p.report_type === 'type1' ? 'Type I' : p.report_type === 'type2' ? 'Type II' : null),
        showWhenAbsent: false,
        legacyRoutes: ['soc2'],
    },
};
const fedrampCr26 = {
    key: 'fedramp-cr26',
    manifestVersion: 1,
    lifecycle: 'active',
    label: 'FedRAMP 20x',
    shortLabel: 'FedRAMP',
    order: 1,
    frameworkId: 'fedramp-cr26',
    profile: {
        columns: {
            certification_class: {
                rule: 'required', values: ['A', 'B', 'C'],
                message: 'FedRAMP profiles need certification_class A, B or C (Class D is not supported yet).',
            },
            path: { rule: 'required', values: ['20x', 'rev5'], message: 'FedRAMP profiles need path 20x or rev5.' },
            report_type: { rule: 'forbidden', message: 'report_type applies to SOC 2 only.' },
        },
        // V106-2-8 (D-106-13): CR26 defines Rev5 certifications for Classes B–D only
        // (every Rev5 subset's applicability lists classes B, C, D; Class A is 20x).
        invalidCombinations: [
            { when: { path: 'rev5', certification_class: 'A' }, message: 'FedRAMP Rev5 has no Class A; use path 20x for Class A, or Class B or C for Rev5.' },
        ],
        // V103-6-1/6-2: offering facts for the FedRAMP schemas + the public listing opt-in.
        options: FedrampProfileOptionsInput,
        storedOptions: FedrampProfileOptions,
        // Class C keeps ≥ 6 months of KSI metrics.
        minRetentionDays: (p) => (p.certification_class === 'C' ? 190 : 90),
    },
    period: { model: 'assessment_window', maxMonths: 12 },
    stage: { model: 'periods', countsInForceObligations: false },
    // ADR-0068 §4: KSIs on the objective model. The engine actually used is the
    // current approved `fedramp-readiness` rule set (migration 341 files
    // {"engine":"objective"}); with none approved the legacy `ksi` engine runs,
    // and a revert is the next version naming `ksi`.
    readiness: {
        engine: 'objective',
        objectiveMode: 'objectives',
        legacyEngine: 'ksi',
        switchRuleSet: 'fedramp-readiness',
        machineValidation: { machineRule: { '20x': 'VDR-TFR-MVX', rev5: 'VDR-TFR-MVF' }, nonMachineRule: 'VDR-TFR-NMV' },
        classApplicability: true,
    },
    // ADR-0079: every active KSI-framework requirement; program controls are the KSIs.
    // Both paths read it (the Rev5 baselines were dropped from v10.6; `path=rev5` is a label).
    requirementSource: { kind: 'framework', programControls: { refPrefix: 'KSI-' } },
    // ADR-0091 §2: in-scope assets = assets of in-boundary resources in a counted environment.
    resourceScope: 'in_boundary_resources',
    scoring: { engine: 'none' },
    deadlines: { vulnerability: 'VDR-TFR-PVR' },
    coverage: { documentKinds: ['fedramp_authorization'], subserviceOrgs: false, federalDataGaps: true },
    operate: { queue: true, contributors: ['findings', 'acceptances', 'scn'] },
    acceptanceSources: ['accepted_weakness'],
    defaultForUntaggedWeakness: true,
    // V109-6-3 / 6-4 (ADR-0091 §5–§6): + per-control / program OSCAL Assessment Results and the POA&M from a sweep.
    packages: { kinds: ['certification_package_overview', 'security_decision_record', 'ongoing_certification_report', 'oscal_ar_control', 'oscal_ar_program', 'oscal_poam'], rulesGated: true },
    features: { significantChangeNotices: true, cds: true, legalDeterminations: false, derivedSweep: true, poam: false, customerRequirements: false, persistentValidation: true, transitionGuidance: true, alternativeFrameworkMaterials: true },
    transition: { securityCategoryOption: 'security_category', notice: 'scn' },
    // ADR-0067 / ADR-0068 §2: the annual independent assessment is by a FedRAMP Recognized assessor.
    assessorTypes: ['fedramp_recognized'],
    onboarding: {
        hint: 'Selling to US federal agencies',
        addProgramLabel: 'Set up FedRAMP Class B profile',
        addProgramDefaults: { certification_class: 'B', path: '20x' },
        // V108-8-15: asked when FedRAMP is added; a change once a period exists starts a new period (determinations
        // on KSIs both classes assess carry).
        askOptions: [
            { key: 'certification_class', label: 'Class', newPeriodOnChange: true, values: [
                    { value: 'A', label: 'Class A' }, { value: 'B', label: 'Class B' }, { value: 'C', label: 'Class C' },
                ] },
        ],
    },
    shell: {
        notApplicable: {}, titles: {}, loopLabel: null, defaultTab: 'stage', operateGate: true,
        panels: { prepare: 'fedramp-prepare', operate: 'ksi-operate', report: 'report-packages' },
    },
    nav: { titleSuffix: noSuffix, showWhenAbsent: false, legacyRoutes: [] },
};
const aiRegulatory = {
    key: 'ai-regulatory',
    manifestVersion: 1,
    lifecycle: 'active',
    label: 'AI Regulatory',
    shortLabel: 'AI Regulatory',
    order: 2,
    frameworkId: null,
    profile: { columns: noProgramColumns, minRetentionDays: minRetention90 },
    period: { model: 'none', maxMonths: null },
    // ADR-0100 §1 (O-1, owner 2026-10-22): a system-scoped program assesses the CIs its in-scope systems link
    // (one synthesized `assessed` asset per CI-in-system); AI machine determinations judge those cells.
    // No periods: no machine rows; the flag drives the requirement-level path only (ADR-0100 §4).
    resourceScope: 'linked_configuration_items',
    stage: { model: 'standing', countsInForceObligations: true },
    readiness: { engine: 'jurisdiction', objectiveMode: 'none' },
    scoring: { engine: 'none' },
    deadlines: { vulnerability: 'ops_sla' },
    coverage: { documentKinds: [], subserviceOrgs: false, federalDataGaps: false },
    operate: { queue: false, contributors: [] },
    acceptanceSources: [],
    defaultForUntaggedWeakness: false,
    packages: { kinds: [], rulesGated: false },
    features: { significantChangeNotices: false, cds: false, legalDeterminations: true, derivedSweep: false, poam: false, customerRequirements: false },
    assessorTypes: [],
    onboarding: { hint: 'Track AI laws where it’s used', addProgramLabel: 'Add AI Regulatory program', addProgramDefaults: {} },
    shell: {
        // V98-4 review T4-H10: 2–4 words, never a sentence.
        notApplicable: { prepare: 'Not applicable', report: 'No report' },
        titles: { audit: 'Conformity' },
        loopLabel: 'In force',
        defaultTab: 'scope',
        operateGate: false,
        panels: { scope: 'ai-scope', operate: 'ai-operate', audit: 'ai-conformity' },
    },
    nav: { titleSuffix: noSuffix, showWhenAbsent: false, legacyRoutes: [] },
};
/**
 * ISO 42001 options (V108-8-15): the certification body the customer has chosen
 * and the planned start of the certification cycle. Both optional; a recorded
 * `cycle_start` milestone wins over the planned date.
 */
export const IsoProfileOptions = z
    .object({
    certification_body: z.string().trim().min(1, 'Name the certification body, or leave it blank.').max(200, 'Use at most 200 characters for the certification body.').optional(),
    cycle_start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Cycle start must be a date (YYYY-MM-DD).')
        .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) && new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v, 'Cycle start must be a real date.')
        .optional(),
})
    .strict();
const iso42001 = {
    key: 'iso-42001',
    manifestVersion: 1,
    lifecycle: 'active',
    label: 'ISO 42001',
    shortLabel: 'ISO 42001',
    order: 3,
    frameworkId: 'iso-42001',
    // V108-8-15: the certification body and the planned cycle start (both optional).
    profile: { columns: noProgramColumns, options: IsoProfileOptions, minRetentionDays: minRetention90 },
    period: { model: 'assessment_window', maxMonths: 12 },
    stage: { model: 'periods', countsInForceObligations: false },
    // ADR-0080 §3: clauses 4-10 + Annex A on the objective engine (no live numbers moved: there were
    // 0 iso-42001 requirements, so the framework engine returned nothing; a hard switch, not a rule set).
    readiness: { engine: 'objective', objectiveMode: 'objectives', scheduledEvaluation: true }, // D-106-18
    // ADR-0080 §3: every active iso-42001 requirement; the 38 Annex A controls are the program controls.
    requirementSource: { kind: 'framework', programControls: { refPrefix: 'A.' } },
    // ADR-0080 §6 / D-106-5: the AIMS scope is the program's ADR-0066 boundary.
    scopeView: { source: 'boundary' },
    // ADR-0100 §1 (O-1, owner 2026-10-22): a system-scoped program assesses the CIs its in-scope systems link
    // (one synthesized `assessed` asset per CI-in-system); AI machine determinations judge those cells.
    resourceScope: 'linked_configuration_items',
    // ADR-0080 §4: the Statement of Applicability rules on the Annex A controls; approval issues a signed iso_soa package.
    soa: { refPrefix: 'A.', packageKind: 'iso_soa' },
    // ADR-0080 §5: stage 1 / stage 2 / surveillance ×2 / recertification from the `iso-certification` rule set (531b).
    certificationCycle: { rulesRef: 'iso-certification' },
    scoring: { engine: 'none' },
    deadlines: { vulnerability: 'ops_sla' },
    coverage: { documentKinds: ['iso_42001', 'iso_27001'], subserviceOrgs: false, federalDataGaps: false },
    operate: { queue: true, contributors: ['findings', 'acceptances'] },
    acceptanceSources: ['risk_register'],
    defaultForUntaggedWeakness: false,
    // V106-5-4 (migration 530): the signed Statement of Applicability.
    packages: { kinds: ['iso_soa'], rulesGated: false },
    features: { significantChangeNotices: false, cds: false, legalDeterminations: false, derivedSweep: false, poam: false, customerRequirements: false },
    assessorTypes: ['certification_body'],
    onboarding: {
        hint: 'AI management system', addProgramLabel: 'Add ISO 42001 program', addProgramDefaults: {},
        // V108-8-15: optional at add; the planned cycle start anchors the certification cycle until one is recorded.
        askOptions: [
            { key: 'certification_body', label: 'Certification body', input: 'text', values: [], hint: 'Optional · who will certify you' },
            { key: 'cycle_start', label: 'Cycle start', input: 'date', values: [], hint: 'Optional · when the certification cycle begins' },
        ],
    },
    // V106-5-6: Scope (boundary scope view + SoA), Prepare (objectives honouring the SoA), Audit (certification
    // body), Operate (the certification cycle).
    shell: { notApplicable: {}, titles: {}, loopLabel: null, defaultTab: 'stage', operateGate: false, panels: { scope: 'iso-scope', prepare: 'iso-prepare', audit: 'iso-audit', operate: 'iso-operate' } },
    nav: { titleSuffix: noSuffix, showWhenAbsent: true, legacyRoutes: [] },
};
/**
 * CMMC options (D-102-4): Level 1 is self-assessment only; Level 2 is self or
 * C3PAO. `cage_codes` + `assessment_scope` are the SPRS entry fields every
 * signed statement carries (D-UX-4, 2026-10-05); signing needs a CAGE code.
 */
export const CmmcProfileOptions = z
    .object({
    level: z.enum(['1', '2']),
    assessment_type: z.enum(['self', 'c3pao']),
    cage_codes: z.array(z.string().regex(/^[A-Z0-9]{5}$/, 'A CAGE code is 5 letters or digits')).min(1).max(50).optional(),
    assessment_scope: z.string().trim().min(1).max(500).optional(),
})
    .strict()
    .refine((o) => !(o.level === '1' && o.assessment_type === 'c3pao'), {
    message: 'CMMC Level 1 is a self-assessment; C3PAO certification applies to Level 2.',
});
/** Planned until v10.2 Track 6: seeded and listed, but a workspace can't add it yet (ADR-0050 §6). */
const cmmc = {
    key: 'cmmc',
    manifestVersion: 1,
    lifecycle: 'planned',
    label: 'CMMC',
    shortLabel: 'CMMC',
    order: 4,
    // Level 1 reuses the 17 mapped Level 2 requirement rows (D-102-4).
    frameworkId: 'cmmc-l2',
    profile: { columns: noProgramColumns, options: CmmcProfileOptions, minRetentionDays: minRetention90 },
    period: { model: 'assessment_window', maxMonths: 12 },
    stage: { model: 'periods', countsInForceObligations: false },
    readiness: { engine: 'objective', objectiveMode: 'objectives', scheduledEvaluation: true }, // D-106-18
    // ADR-0057 §2 (as amended): the five 32 CFR 170.19 asset categories.
    // Security Protection Assets are assessed only against the requirements
    // relevant to the capability they provide; CRMA may get a limited check.
    scoping: {
        categories: [
            { key: 'cui', label: 'CUI Asset', treatment: 'assessed' },
            { key: 'security_protection', label: 'Security Protection Asset', treatment: 'assessed' },
            { key: 'contractor_risk_managed', label: 'Contractor Risk Managed Asset', treatment: 'documented' },
            { key: 'specialized', label: 'Specialized Asset', treatment: 'documented' },
            { key: 'out_of_scope', label: 'Out-of-Scope Asset', treatment: 'excluded' },
        ],
    },
    scoring: { engine: 'cmmc', rulesRef: 'cmmc-program-rules' },
    deadlines: { vulnerability: 'ops_sla' },
    // External service providers holding CUI need FedRAMP Moderate or equivalent (DFARS 252.204-7012).
    coverage: { documentKinds: ['fedramp_authorization'], subserviceOrgs: false, federalDataGaps: false },
    operate: { queue: true, contributors: ['findings', 'acceptances'] },
    acceptanceSources: ['accepted_weakness'],
    defaultForUntaggedWeakness: false,
    // ADR-0058 §6: SSP + POA&M (V102-6-5); the C3PAO package arrives with Track 7.
    packages: { kinds: ['cmmc_ssp', 'cmmc_poam', 'c3pao_package'], rulesGated: true },
    features: { significantChangeNotices: false, cds: false, legalDeterminations: false, derivedSweep: false, poam: true, customerRequirements: true },
    assessorTypes: ['c3pao'],
    onboarding: {
        hint: 'Defense contracts with FCI or CUI',
        addProgramLabel: 'Set up CMMC Level 2 self-assessment',
        addProgramDefaults: { level: '2', assessment_type: 'self' },
    },
    // V102-8-2: Prepare leads with the scorecard; Audit is the existing period (fieldwork pins the tests) + the affirmation gate.
    shell: { notApplicable: {}, titles: {}, loopLabel: null, defaultTab: 'stage', operateGate: false,
        panels: { scope: 'cmmc-scope', prepare: 'cmmc-scorecard', audit: 'cmmc-audit' } },
    nav: { titleSuffix: noSuffix, showWhenAbsent: false, legacyRoutes: [] },
};
/**
 * GovRAMP options (D-103-3, D-103-12): the tier and the impact level are
 * profile fields. Provisional (Provisionally Authorized) assesses the full
 * Authorized set. v10.3 loads the Moderate baseline only, so Low and High are
 * refused until their baselines exist.
 */
export const GOVRAMP_TIERS = ['snapshot', 'core', 'ready', 'authorized', 'provisional'];
export const GovrampProfileOptions = z
    .object({
    tier: z.enum(GOVRAMP_TIERS, { error: 'GovRAMP tier must be snapshot, core, ready, authorized or provisional.' }),
    // V106-3-2 (D-106-4): widened to the impact levels whose baselines load (519 Low, 520 High).
    impact_level: z.enum(['low', 'moderate', 'high'], { error: 'GovRAMP impact level must be low, moderate or high.' }),
})
    .strict();
/**
 * Active since V103-3-7 (migration 349b flips the `programs` row to match).
 *
 * Periods: every tier runs on `assessment_window` periods (POA&M items are
 * per period). A Snapshot / Core period is the 12-month status term the
 * annual resubmission renews; quarterly Snapshots and ConMon cycles run
 * inside it (rule set `conmon`, cycles in V103-3-5). A Ready / Authorized
 * period is the annual 3PAO assessment window, with monthly ConMon inside.
 * A tier change opens a new period (V103-3-4).
 */
const govramp = {
    key: 'govramp',
    manifestVersion: 1,
    lifecycle: 'active',
    label: 'GovRAMP',
    shortLabel: 'GovRAMP',
    order: 5,
    frameworkId: 'nist-800-53',
    profile: { columns: noProgramColumns, options: GovrampProfileOptions, minRetentionDays: minRetention90 },
    period: { model: 'assessment_window', maxMonths: 12 },
    stage: { model: 'periods', countsInForceObligations: false },
    readiness: { engine: 'objective', objectiveMode: 'objectives', scheduledEvaluation: true }, // D-106-18
    // ADR-0064 §4: baseline by impact level, members by tier.
    requirementSource: {
        baselineOption: 'impact_level',
        baselines: { low: 'govramp-low', moderate: 'govramp-moderate', high: 'govramp-high' },
        tierOption: 'tier',
        tierAliases: { provisional: 'authorized' },
        // ADR-0079 §5: tiers per impact level (V106-3-2). Snapshot and Core exist at Moderate only
        // (GovRAMP SAF v4.2 App. C); Low has Ready 29 (D-106-15); High is Authorized only (D-106-16).
        allowedTiers: {
            low: ['ready', 'authorized', 'provisional'],
            moderate: GOVRAMP_TIERS,
            high: ['authorized', 'provisional'],
        },
    },
    // ADR-0091 §2: in-scope assets = assets of in-boundary resources in a counted environment.
    resourceScope: 'in_boundary_resources',
    scoring: { engine: 'govramp-snapshot', rulesRef: 'govramp' },
    deadlines: { vulnerability: 'ops_sla' },
    coverage: { documentKinds: [], subserviceOrgs: false, federalDataGaps: false },
    operate: { queue: true, contributors: ['findings', 'acceptances'] },
    acceptanceSources: ['accepted_weakness'],
    defaultForUntaggedWeakness: false,
    // V103-7-2..7-5 (migration 340): native GovRAMP workbooks, the ConMon bundle, the Core submission, an OSCAL SSP.
    // V109-6-3 / 6-4 (ADR-0091 §5–§6): + per-control / program OSCAL Assessment Results and the POA&M from a sweep.
    packages: { kinds: ['govramp_ssp_xlsx', 'govramp_poam_xlsx', 'govramp_conmon_bundle', 'govramp_core_submission', 'oscal_ssp', 'oscal_ar_control', 'oscal_ar_program', 'oscal_poam'], rulesGated: false },
    features: { significantChangeNotices: false, cds: false, legalDeterminations: false, derivedSweep: false, poam: true, customerRequirements: false, transitionGuidance: true },
    transition: { securityCategoryOption: 'impact_level', notice: 'change_notice' },
    // ADR-0067: GovRAMP 3PAOs record Ready / Authorized determinations (live gate, V103-5-3).
    assessorTypes: ['govramp_3pao'],
    onboarding: {
        hint: 'Selling to state and local government',
        addProgramLabel: 'Set up GovRAMP Core',
        addProgramDefaults: { tier: 'core', impact_level: 'moderate' },
        // V103-3-7 / V106-3-2: onboarding asks the impact level, then the tier; the tiers offered are the
        // ones the level's baseline allows (requirementSource.allowedTiers, via askOptionValues).
        askOptions: [
            { key: 'impact_level', label: 'Impact level', lockedOnceAssessed: true, values: [
                    { value: 'low', label: 'Low' }, { value: 'moderate', label: 'Moderate' }, { value: 'high', label: 'High' },
                ] },
            { key: 'tier', label: 'Tier', newPeriodOnChange: true, values: [
                    { value: 'snapshot', label: 'Snapshot' }, { value: 'core', label: 'Core' }, { value: 'ready', label: 'Ready' },
                    { value: 'authorized', label: 'Authorized' }, { value: 'provisional', label: 'Provisional' },
                ] },
        ],
    },
    // V103-3-6: Prepare and Operate lead with the GovRAMP status, ConMon, readiness and tier change;
    // Audit records GovRAMP's decision and the Ready booking. Scope / Report stay generic.
    shell: { notApplicable: {}, titles: {}, loopLabel: null, defaultTab: 'stage', operateGate: false,
        panels: { prepare: 'govramp-prepare', operate: 'govramp-program', audit: 'govramp-audit' } },
    nav: { titleSuffix: noSuffix, showWhenAbsent: false, legacyRoutes: [] },
};
const REGISTRY = {
    soc2,
    'fedramp-cr26': fedrampCr26,
    'ai-regulatory': aiRegulatory,
    'iso-42001': iso42001,
    cmmc,
    govramp,
};
export const PROGRAM_MANIFESTS = Object.freeze(Object.values(REGISTRY).sort((a, b) => a.order - b.order));
const registryErrors = [
    ...Object.entries(REGISTRY).filter(([k, m]) => k !== m.key).map(([k, m]) => `registry key ${k} holds manifest ${m.key}`),
    ...validateProgramRegistry(PROGRAM_MANIFESTS),
];
if (registryErrors.length)
    throw new Error(`Invalid program registry: ${registryErrors.join('; ')}`);
const BY_KEY = new Map(PROGRAM_MANIFESTS.map((m) => [m.key, m]));
export function getProgramManifest(key) {
    return BY_KEY.get(key);
}
/** Program keys a workspace can add today, in display order. */
export function activeProgramKeys() {
    return PROGRAM_MANIFESTS.filter((m) => m.lifecycle === 'active').map((m) => m.key);
}
/** Every key the DB accepts (`programs` rows, migration 273a): active and planned. */
export function allProgramKeys() {
    return PROGRAM_MANIFESTS.map((m) => m.key);
}
/** The program whose acceptance sources claim untagged accepted weaknesses, if any. */
export function untaggedWeaknessProgram() {
    return PROGRAM_MANIFESTS.find((m) => m.defaultForUntaggedWeakness)?.key ?? null;
}
/**
 * Checks a profile's program columns and options against its manifest.
 * Returns the first problem's message (stable text API clients show), or null.
 */
export function profileProblem(m, input) {
    // Declared order decides which message wins (it matches the pre-manifest checks).
    for (const [col, r] of Object.entries(m.profile.columns)) {
        const v = input[col] ?? null;
        if (r.rule === 'required' && (v == null || (r.values && !r.values.includes(v))))
            return r.message;
        if (r.rule === 'forbidden' && v != null)
            return r.message;
    }
    for (const col of ['certification_class', 'path', 'report_type']) {
        if (!m.profile.columns[col] && (input[col] ?? null) != null)
            return NOT_FOR_THIS_PROGRAM;
    }
    for (const combo of m.profile.invalidCombinations ?? []) {
        const cols = Object.entries(combo.when);
        if (cols.length && cols.every(([col, v]) => (input[col] ?? null) === v))
            return combo.message;
    }
    if (m.profile.options) {
        const parsed = m.profile.options.safeParse(input.program_options ?? {});
        if (!parsed.success)
            return parsed.error.issues[0]?.message ?? 'Invalid program options.';
        // ADR-0079 §5: tiers allowed per baseline (GovRAMP Snapshot/Core at Moderate only).
        const tierProblem = requirementSourceProfileProblem(m, { ...input, options: parsed.data });
        if (tierProblem)
            return tierProblem;
    }
    else if (input.program_options != null && Object.keys(input.program_options).length) {
        return 'This program takes no options.';
    }
    return null;
}
const PROFILE_COLUMN_KEYS = ['report_type', 'certification_class', 'path'];
/**
 * The create-profile fields for a program: its `onboarding.addProgramDefaults`
 * with `chosen` on top, split into profile columns and `program_options`
 * (GovRAMP tier / impact level, CMMC level). Options are only sent for a
 * program that declares `profile.options`.
 */
export function programCreateFields(m, chosen = {}) {
    const merged = { ...m.onboarding.addProgramDefaults, ...chosen };
    const out = {};
    const options = {};
    for (const [k, v] of Object.entries(merged)) {
        // V108-8-15: a blank optional text / date field is "skipped", never stored.
        if (v === undefined || v === null || (typeof v === 'string' && !v.trim()) || k === 'program')
            continue;
        if (PROFILE_COLUMN_KEYS.includes(k))
            out[k] = v;
        else
            options[k] = v;
    }
    if (m.profile.options && Object.keys(options).length)
        out.program_options = options;
    return out;
}
export function programAddFields(m) {
    return (m.onboarding.askOptions ?? []).map((o) => {
        const input = o.input ?? 'choice';
        return {
            key: o.key, label: o.label, input, values: o.values, hint: o.hint ?? null,
            target: PROFILE_COLUMN_KEYS.includes(o.key) ? 'column' : 'option',
            default: m.onboarding.addProgramDefaults[o.key] ?? null,
            optional: input !== 'choice',
            newPeriodOnChange: !!o.newPeriodOnChange,
            lockedOnceAssessed: !!o.lockedOnceAssessed,
        };
    });
}
/**
 * The options first-run onboarding asks in its program step: choice options that are
 * not profile columns (onboarding asks report type / class with its own questions).
 */
export function onboardingAskOptions(m) {
    return (m.onboarding.askOptions ?? []).filter((o) => (o.input ?? 'choice') === 'choice' && !PROFILE_COLUMN_KEYS.includes(o.key));
}
/** V108-8-15: a profile's current value of each key field (columns, then stored options), as strings; absent = null. */
export function programFieldValues(m, profile) {
    const options = storedProfileOptions(m, profile.program_options_json ?? null);
    const out = {};
    for (const f of programAddFields(m)) {
        const v = f.target === 'column' ? profile[f.key] : options[f.key];
        out[f.key] = typeof v === 'string' && v ? v : null;
    }
    return out;
}
/** Programs in display order whose manifest matches `pred`; active only unless `planned` is set. */
export function programsWhere(pred, opts = {}) {
    return PROGRAM_MANIFESTS.filter((m) => (opts.planned || m.lifecycle === 'active') && pred(m));
}
/** True when `key` names a program whose manifest matches `pred` (unknown keys never match). */
export function programHas(key, pred) {
    const m = key ? getProgramManifest(key) : undefined;
    return !!m && pred(m);
}
/**
 * The manifest acceptance source for a canonical acceptance source
 * (acceptance-service `ACCEPTANCE_SOURCES`): security risks and register
 * exceptions are both the information-security risk register.
 */
export function programAcceptanceSource(source) {
    return source === 'accepted_weakness' ? 'accepted_weakness' : 'risk_register';
}
/**
 * The one acceptance rule (ADR-0050 §3): an acceptance belongs to the
 * programs whose `acceptanceSources` include its source. An accepted weakness
 * tagged with a program (its finding's `certification_program`) belongs to
 * that program; an untagged one belongs to `untaggedWeaknessProgram()`.
 */
export function acceptanceBelongsTo(program, a) {
    const src = programAcceptanceSource(a.source);
    if (src === 'accepted_weakness' && a.program)
        return a.program === program;
    const m = getProgramManifest(program);
    if (!m || !m.acceptanceSources.includes(src))
        return false;
    return src === 'accepted_weakness' ? untaggedWeaknessProgram() === program : true;
}
/** Programs (display order, active and planned) an acceptance belongs to. */
export function programsForAcceptance(a) {
    return PROGRAM_MANIFESTS.filter((m) => acceptanceBelongsTo(m.key, a)).map((m) => m.key);
}
/**
 * A profile's stored program options (ADR-0079): parsed with the manifest's
 * `profile.storedOptions` when it declares one (invalid → {}), else the raw
 * JSON object. Never throws.
 */
export function storedProfileOptions(m, json) {
    let raw;
    try {
        raw = JSON.parse(json || '{}');
    }
    catch {
        return {};
    }
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return {};
    if (!m?.profile.storedOptions)
        return raw;
    const parsed = m.profile.storedOptions.safeParse(raw);
    return parsed.success ? parsed.data : {};
}
/**
 * The certification_timeframes rule for machine validation on a profile's path
 * (ADR-0068 §3, `readiness.machineValidation.machineRule`). Paths the manifest
 * doesn't list read the last declared rule (FedRAMP: anything but 20x → rev5's).
 */
export function machineValidationRule(m, path) {
    const rules = m.readiness.machineValidation?.machineRule;
    if (!rules)
        throw new Error(`${m.key} has no machine validation`);
    const declared = Object.values(rules);
    return rules[path] ?? declared[declared.length - 1];
}
/** Programs (display order, active and planned) whose manifest lets assessor firms of `type` assess them. */
export function programsForAssessorType(type) {
    return PROGRAM_MANIFESTS.filter((m) => m.assessorTypes.includes(type)).map((m) => m.key);
}
/** The active program legal applicability determinations are decided in (AI Regulatory), if any. */
export function legalDeterminationsProgram() {
    return programsWhere((m) => m.features.legalDeterminations)[0]?.key ?? null;
}
//# sourceMappingURL=manifests.js.map